import type { EditionRail } from "../ui/edition-rail.js";

/**
 * What the watch page (`/edition/<id>`, `round-route.ts` `editionWatchPage`) keeps
 * while the visitor moves between boards and rounds of its event (owner,
 * 2026-09-23: "I clicked on a game and it refreshed the page and reset
 * everything"). The router hands the page every new address of its route
 * (`router.ts` `handleInPlace`); the page answers it with what this holds:
 *
 * - `rail`: the tournament's one game rail. It outlives the round page that
 *   shows it — search, filters, open matches, tab, page and the loaded bundle
 *   survive a round switch; the next round page re-binds it (`EditionRail.rebind`).
 * - `board`: the mounted round page's in-place board switch. True = it showed
 *   the address's board (its own round, or a section of its merged round);
 *   false = another round, which the page mounts beside the rail.
 *
 * Types only from the rail: this module is in the entry chunk with the page.
 */
export interface WatchSession {
  readonly editionId: string;
  rail: EditionRail | null;
  board: ((query: URLSearchParams) => boolean) | null;
  /**
   * The event is over (the shared projection's `eventFinished`) and the address named no round:
   * the round page opens with the final standings in the center, not a board.
   */
  finalStandings: boolean;
}

let current: WatchSession | null = null;

export function openWatchSession(editionId: string): WatchSession {
  if (current !== null) closeWatchSession(current);
  current = { editionId, rail: null, board: null, finalStandings: false };
  return current;
}

/** The open session that covers `editionId`: the page's own edition, or a section its rail lists (Open and Women). */
export function watchSessionFor(editionId: string): WatchSession | null {
  if (current === null || editionId === "") return null;
  return current.editionId === editionId || (current.rail?.editionIds().includes(editionId) ?? false) ? current : null;
}

export function closeWatchSession(session: WatchSession): void {
  if (current === session) current = null;
  session.board = null;
  const rail = session.rail;
  session.rail = null;
  rail?.close();
}
